import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { RoutePicker } from '@features/navigation/components/RoutePicker'
import { SETTING_DEFINITIONS, useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { headerButtonClass } from '@features/shared/components/headerButtonClasses'
import { IconButton } from '@features/shared/components/IconButton'
import { CloseIcon } from '@features/shared/icons/icons'
import { createLayoutResizeObserver } from '@features/shared/utils/layoutResizeObserver'
import { useDescribedPaths } from '../hooks/useDescribedPaths'
import { useSetHomePage } from '../hooks/useFavorites'
import { FavoriteTrail } from './FavoritesMenu'
import { measureRailAnchor, RAIL_MODULE_ATTRIBUTE } from '../logic/railAnchor'
import { placeRailCard } from '../logic/railCard'
import { RAIL_FLYOUT_CARD_CLASS, RAIL_FLYOUT_LEFT_CLASS } from './ModuleFlyout'

const HOME_ITEM = 'home'
const HOME_ITEM_SELECTOR = `[${RAIL_MODULE_ATTRIBUTE}="${HOME_ITEM}"]`

/**
 * Home's pencil: a card where Home's name card was, its title band level with
 * Home's icon and reading as the name card did (Home | Tables › Accounts, the
 * page as picked so far), then the Home Page preference's own picker's fields
 * (RoutePicker, as on the Preferences page) right in the card, then Cancel
 * and Save.
 * Saving makes the picked page Home ("Home Page set"). Escape, Cancel, the
 * close button or a click outside it (but in its pickers' lists) closes it.
 */
function HomePageEditorCard({ onClose }: { onClose: () => void }) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState(0)
  const homePage = useSettingsStore((state) => state.homePage)
  const saved = homePage ?? SETTING_DEFINITIONS.homePage.systemDefault
  const [draft, setDraft] = useState<string | null>(saved)
  const setHomePage = useSetHomePage()
  const draftPaths = useMemo(() => (draft ? [draft] : null), [draft])
  const draftParts = useDescribedPaths(draftPaths)?.[0]?.parts
  const draftName = draftParts?.join(' › ')

  // Its title band level with Home's icon, as the name card was; placed
  // before the browser paints, so it never shows in the wrong spot.
  useLayoutEffect(() => {
    const card = cardRef.current
    const layer = wrapperRef.current?.offsetParent
    if (!card || !(layer instanceof HTMLElement)) return
    const place = () => {
      const { tileCenter, top: minTop, room } = measureRailAnchor(layer, HOME_ITEM)
      if (tileCenter === null) return
      // The band's middle, below the card's own border.
      const band = headerRef.current
      const bandMiddle = band ? band.offsetTop + band.offsetHeight / 2 : 0
      const bottomLimit = minTop + Math.max(0, room - card.offsetHeight)
      const next = placeRailCard(tileCenter - bandMiddle, bottomLimit, minTop)
      setTop((current) => (current === next ? current : next))
    }
    place()
    const observer = createLayoutResizeObserver(place)
    observer.observe(card)
    observer.observe(layer)
    return () => observer.disconnect()
  }, [])

  // A click outside it closes it, but not in its pickers' lists (portals).
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (cardRef.current?.contains(target) || target.closest('[data-portal]')) return
      onClose()
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [onClose])

  useEffect(() => {
    cardRef.current?.focus()
  }, [])

  const closeToRail = () => {
    onClose()
    document.querySelector<HTMLElement>(HOME_ITEM_SELECTOR)?.focus()
  }
  const changed = draft !== null && draft !== saved
  const save = () => {
    if (changed) setHomePage(draft, draftName ?? draft)
    closeToRail()
  }

  return (
    <div
      ref={wrapperRef}
      // Fades in as it opens, as a menu does (rail-flyout-fade).
      data-shown="true"
      className={`rail-flyout-fade absolute ${RAIL_FLYOUT_LEFT_CLASS} z-20`}
      style={{ top }}
    >
      <section
        ref={cardRef}
        role="dialog"
        aria-label="Change Home Page"
        tabIndex={-1}
        className={`flex w-[22rem] flex-col overflow-hidden text-[color:var(--sidebar-submenu-text-subtle)] outline-none ${RAIL_FLYOUT_CARD_CLASS}`}
        onKeyDown={(event) => {
          // A list open inside it closes first (it handles its own Escape).
          if (event.key !== 'Escape' || event.defaultPrevented) return
          event.preventDefault()
          closeToRail()
        }}
      >
        {/* As tall as Home's name card, so its title sits where the name was. */}
        <div
          ref={headerRef}
          className="flex h-[calc(var(--sidebar-tile-size)+2*(var(--sidebar-item-inset)+1px))] shrink-0 items-center justify-between gap-2 border-b border-[color:var(--sidebar-submenu-border)] pl-3 pr-1.5"
        >
          {/* Home | its page, as Home's name card reads. */}
          <h2 className="flex min-w-0 items-center gap-2 whitespace-nowrap text-sm font-medium text-[color:var(--sidebar-submenu-text-default)]">
            Home
            <span
              aria-hidden="true"
              className="h-sidebar-module-size w-px shrink-0 bg-[color:var(--border-strong)]"
            />
            <span className="flex min-w-0 font-normal">
              {draftParts ? (
                <FavoriteTrail parts={draftParts} isActive={false} />
              ) : (
                <span className="text-[color:var(--sidebar-submenu-text-faint)]">…</span>
              )}
            </span>
          </h2>
          <IconButton
            label="Close"
            onClick={closeToRail}
            className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--sidebar-submenu-text-faint)] transition-colors hover:bg-[color:var(--sidebar-item-hover-bg)] hover:text-[color:var(--sidebar-submenu-text-default)]"
          >
            <CloseIcon size={12} aria-hidden="true" />
          </IconButton>
        </div>
        {/* The Preferences page's picker's fields, right in the card. */}
        <div className="p-3">
          <RoutePicker value={draft} onChange={setDraft} ariaLabel="Home Page" layout="fields" />
        </div>
        {/* Its actions under a line, as a dialog's, in a short footer. */}
        <div className="flex items-center justify-end gap-2 border-t border-[color:var(--sidebar-submenu-border)] px-3 py-2">
          <button type="button" className={headerButtonClass('secondary')} onClick={closeToRail}>
            Cancel
          </button>
          <button type="button" className={headerButtonClass('primary')} onClick={save}>
            Save
          </button>
        </div>
      </section>
    </div>
  )
}

/** Home's pencil's card while it's open (HomePageEditorCard); fresh each time. */
export default function HomePageEditor({ open, onClose }: { open: boolean; onClose: () => void }) {
  return open ? <HomePageEditorCard onClose={onClose} /> : null
}
